import { IMeasurement, IMeasurementTags } from '@agoda/messaging-client';
import { TrafficTypeCronos } from 'core-framework/constant/Traffic.types';
import { getBootstrapConfig } from 'core/window/windowService';
import { BootstrapConfig, Geo } from 'core/type/BootstrapConfig.types';
import { ActivityWithContent } from 'gql/autogen/ActivityWithContent';

let isInitial = true;

const markInitialCall = (tags: IMeasurementTags): IMeasurementTags => {
    tags.initialCall = isInitial ? 'true' : 'false';
    isInitial = false;
    return tags;
};

const formatGeoName = (name = '') => {
    if (!name) return 'undefined';
    return name.replace(/_/g, ' ');
};

export const getGeoLogTags = (config: BootstrapConfig) => {
    const geo = config.geo || ({ cityId: 0, countryId: 0 } as Geo);
    return {
        id: (geo.id || 0).toString(),
        name: formatGeoName(geo.name),
        activityId: (geo.activityId || 0).toString(),
        cityId: (geo.cityId || 0).toString(),
        cityName: formatGeoName(geo.cityName),
        countryId: (geo.countryId || 0).toString(),
        countryName: formatGeoName(geo.countryName),
        countryISO2: (geo.countryISO2 && geo.countryISO2.toUpperCase()) || 'undefined',
        areaId: (geo.areaId || 0).toString(),
        areaName: formatGeoName(geo.areaName),
        stateName: formatGeoName(geo.stateName),
    };
};

export const getGeoLogTagsFromActivity = (geoLogTags: IMeasurementTags, activity?: ActivityWithContent) => {
    if (!activity?.content?.activity?.location) {
        return geoLogTags;
    }
    const { city, country } = activity.content.activity.location;
    return {
        ...geoLogTags,
        activityId: String(activity.masterActivityId ?? geoLogTags.activityId),
        cityId: String(city?.id ?? geoLogTags.cityId),
        cityName: String(city?.name ?? geoLogTags.cityName),
        countryId: String(country?.id ?? geoLogTags.countryId),
        countryName: String(country?.name ?? geoLogTags.countryName),
    };
};

export const getMandatoryMeasurementTags = (config: BootstrapConfig): Record<string, string> => {
    const requestProfile = config.requestProfile || undefined;
    const pageConfig = config.pageConfig || {};
    const trafficType: TrafficTypeCronos = config.requestProfile?.trafficType || TrafficTypeCronos.NA;
    const userDevice = config.userDevice || {};
    const browser = config.browser || {};

    const geoTags = getGeoLogTags(config);

    return {
        ...geoTags,
        botName: requestProfile?.knownBotName || 'na',
        browserName: browser.browserName,
        cid: String(pageConfig.cid),
        cttp: String(pageConfig.cttp),
        currencyCode: String(config.currency.code),
        deviceType: userDevice.deviceType,
        htmlLanguage: pageConfig.htmlLanguage,
        isBot: String(config.isBot),
        isDebug: String(config.isDebug),
        isDesktop: String(browser.isDesktop),
        isEdge: String(browser.isEdge),
        isIE: String(browser.isIE),
        isInternal: String(trafficType === TrafficTypeCronos.Internal),
        isLoggedIn: String(config.isLoggedIn),
        isMobile: String(browser.isMobile),
        isTablet: String(browser.isTablet),
        isWebView: String(config.isWebView),
        language: config.language?.locale?.toLowerCase(),
        locale: config.language?.locale?.toLowerCase(),
        loginLevel: String(pageConfig.loginLvl),
        mobileDeviceType: userDevice.mobileDeviceType,
        origin: pageConfig.origin,
        sessionId: pageConfig.sessionId,
        trafficGroupId: String(pageConfig.trafficGroupId),
        trafficSubGroupId: String(pageConfig.trafficSubGroupId),
        trafficType: String(trafficType),
        userId: pageConfig.userId || 'unknown',
        whiteLabelId: String(pageConfig.whitelabelid),
        DeviceId: pageConfig.userId || 'unknown',
        MemberId: String(config.memberId),
    };
};

const buildTags = (operationName: string, pageTypeId: number, config = getBootstrapConfig()): IMeasurementTags => {
    const baseTags = getMandatoryMeasurementTags(config);

    return {
        ...baseTags,
        type: 'measurement', // This tag will help identify the source of log message
        operation: operationName,
        pageTypeId: String(pageTypeId),
        srcUrl: '',
    };
};

export const buildMeasurement = (
    operationName: string,
    name: string,
    pageTypeId: number,
    message = '',
    measurement = 1,
    addInitialCallTag = false,
    config = getBootstrapConfig(),
): IMeasurement & { tags: IMeasurementTags } => {
    const tags = buildTags(operationName, pageTypeId, config);
    tags.subcategory = name.substring(name.lastIndexOf('.') + 1).toLowerCase();

    if (message) {
        tags.message = message;

        if (message.includes(':')) {
            const parts = message.split(':');
            tags.subcategory1 = (parts[0] || '').trim();
            if (parts.length > 2) {
                tags.subcategory2 = (parts[1] || '').trim();
            }
        }
    }

    return {
        name,
        measurement,
        count: 1,
        tags: addInitialCallTag ? markInitialCall(tags) : tags,
    };
};
